import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightIcon, HeartIcon, OfficeBuildingIcon, SearchIcon, SparklesIcon } from "@heroicons/react/solid";
import { BACKEND_URL } from "../config";
import { useAuth } from "../hooks/useAuth";
import { getActiveOffers } from "../utils/offerUtils";

const emptyPrefs = {
  interestedCountries: [],
  preferredCourses: [],
  budget: "",
  studyPlan: "",
};

function normalizeArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") return value.split(",").map((item) => item.trim()).filter(Boolean);
  return [];
}

function completionItems(user, preferences) {
  return [
    Boolean(user?.name),
    Boolean(user?.email),
    Boolean(user?.phone),
    normalizeArray(preferences.interestedCountries).length > 0,
    normalizeArray(preferences.preferredCourses).length > 0,
    Boolean(preferences.budget),
    Boolean(preferences.studyPlan),
  ];
}

function StatCard({ label, value, tone = "cyan" }) {
  const tones = {
    cyan: "border-cyan-200/20 bg-cyan-300/10 text-cyan-100",
    emerald: "border-emerald-200/20 bg-emerald-300/10 text-emerald-100",
    amber: "border-amber-200/20 bg-amber-300/10 text-amber-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <p className="text-2xl font-semibold text-white">{value}</p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-[2px] text-current/80">{label}</p>
    </div>
  );
}

function SectionShell({ title, action, children }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function StudentDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(emptyPrefs);
  const [wishlist, setWishlist] = useState([]);
  const [consultancies, setConsultancies] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function loadDashboard() {
      if (!token) return;
      setLoading(true);
      try {
        const [prefsRes, wishlistRes, consultanciesRes] = await Promise.all([
          fetch(`${BACKEND_URL}/student/preferences`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${BACKEND_URL}/wishlist`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${BACKEND_URL}/consultancies?page=1&limit=12&sort=recommended`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!alive) return;

        if (prefsRes.ok) {
          const prefs = await prefsRes.json();
          setPreferences({
            interestedCountries: normalizeArray(prefs.interestedCountries),
            preferredCourses: normalizeArray(prefs.preferredCourses),
            budget: prefs.budget || "",
            studyPlan: prefs.studyPlan || "",
          });
        }

        if (wishlistRes.ok) {
          const saved = await wishlistRes.json();
          setWishlist(Array.isArray(saved) ? saved : []);
        }

        if (consultanciesRes.ok) {
          const payload = await consultanciesRes.json();
          const rows = Array.isArray(payload) ? payload : payload.data || [];
          setConsultancies(rows);
        }
      } catch (err) {
        console.error("Student dashboard load error", err);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadDashboard();
    return () => {
      alive = false;
    };
  }, [token]);

  const firstName = (user?.name || "there").trim().split(/\s+/)[0];
  const completion = useMemo(() => {
    const items = completionItems(user, preferences);
    return Math.round((items.filter(Boolean).length / items.length) * 100);
  }, [preferences, user]);

  const preferredCountry = normalizeArray(preferences.interestedCountries)[0];
  const recommended = useMemo(() => {
    if (!preferredCountry) return consultancies.slice(0, 4);
    const country = preferredCountry.toLowerCase();
    return consultancies
      .filter((item) =>
        [...(item.countries || []), ...(item.countriesServed || []), item.country, item.city, item.state]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(country)
      )
      .slice(0, 4);
  }, [consultancies, preferredCountry]);

  const activeOffers = useMemo(
    () =>
      consultancies
        .flatMap((consultancy) =>
          getActiveOffers(consultancy.offers || []).map((offer) => ({
            ...offer,
            consultancyId: consultancy._id,
            consultancyName: consultancy.name,
          }))
        )
        .slice(0, 3),
    [consultancies]
  );

  const handleSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("search", query.trim());
    navigate(`/consultancies${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-10">
        <section className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950/50 p-5 sm:p-7">
          <div className="grid gap-6 lg:grid-cols-[1.4fr_0.8fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[4px] text-cyan-200">My Study Abroad</p>
              <h1 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">Hi, {firstName}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Manage preferences, saved consultancies, offers and connections in one place.
              </p>
              <form onSubmit={handleSearch} className="mt-6 flex flex-col gap-3 sm:flex-row">
                <label className="relative flex-1">
                  <span className="sr-only">Search consultancies, countries or cities</span>
                  <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search consultancies, countries or cities"
                    className="w-full rounded-xl border border-white/15 bg-white/[0.08] py-3 pl-12 pr-4 text-sm text-white outline-none placeholder:text-slate-400 focus:border-cyan-300"
                  />
                </label>
                <button className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200">
                  Explore <ArrowRightIcon className="h-4 w-4" />
                </button>
              </form>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <StatCard label="Profile" value={`${completion}%`} />
              <StatCard label="Saved" value={wishlist.length} tone="emerald" />
              <StatCard label="Offers" value={activeOffers.length} tone="amber" />
            </div>
          </div>
        </section>

        <div className="mt-6 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <SectionShell
            title="Study Preferences"
            action={<Link to="/student/preferences" className="text-sm font-semibold text-cyan-200 hover:text-cyan-100">Edit</Link>}
          >
            <div className="grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
              <p><span className="block text-xs uppercase tracking-[2px] text-slate-500">Countries</span>{normalizeArray(preferences.interestedCountries).join(", ") || "Not added"}</p>
              <p><span className="block text-xs uppercase tracking-[2px] text-slate-500">Courses</span>{normalizeArray(preferences.preferredCourses).join(", ") || "Not added"}</p>
              <p><span className="block text-xs uppercase tracking-[2px] text-slate-500">Budget</span>{preferences.budget || "Not added"}</p>
              <p><span className="block text-xs uppercase tracking-[2px] text-slate-500">Intake / Plan</span>{preferences.studyPlan || "Not added"}</p>
            </div>
          </SectionShell>

          <SectionShell
            title="Recommended Consultancies"
            action={<Link to="/consultancies" className="text-sm font-semibold text-cyan-200 hover:text-cyan-100">View all</Link>}
          >
            {!loading && recommended.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-4 text-sm text-slate-300">
                Complete your study preferences to get better recommendations.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {recommended.slice(0, 4).map((item) => (
                  <Link key={item._id} to={`/consultancy/${item._id}`} className="rounded-xl border border-white/10 bg-slate-950/45 p-4 transition hover:border-cyan-200/40">
                    <OfficeBuildingIcon className="h-5 w-5 text-cyan-200" />
                    <p className="mt-3 line-clamp-2 font-semibold text-white">{item.name}</p>
                    <p className="mt-1 text-xs text-slate-400">{[item.city, item.state].filter(Boolean).join(", ") || "Location available on profile"}</p>
                  </Link>
                ))}
              </div>
            )}
          </SectionShell>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <SectionShell title="Saved Consultancies" action={<Link to="/wishlist" className="text-sm font-semibold text-cyan-200 hover:text-cyan-100">View all</Link>}>
            {wishlist.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-4 text-sm text-slate-300">
                Save consultancies while browsing to compare them later.
              </div>
            ) : (
              <div className="space-y-3">
                {wishlist.slice(0, 3).map((item) => (
                  <Link key={item._id} to={`/consultancy/${item.consultancy?._id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-950/45 p-3">
                    <HeartIcon className="h-5 w-5 text-rose-200" />
                    <span className="line-clamp-1 text-sm font-semibold">{item.consultancy?.name || "Consultancy"}</span>
                  </Link>
                ))}
              </div>
            )}
          </SectionShell>

          <SectionShell title="Current Offers" action={<Link to="/offers" className="text-sm font-semibold text-cyan-200 hover:text-cyan-100">View offers</Link>}>
            {activeOffers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/15 p-4 text-sm text-slate-300">
                No active offers are available in the loaded consultancy set.
              </div>
            ) : (
              <div className="space-y-3">
                {activeOffers.map((offer) => (
                  <Link key={`${offer.consultancyId}-${offer._id || offer.title}`} to={`/consultancy/${offer.consultancyId}`} className="block rounded-xl border border-white/10 bg-slate-950/45 p-3">
                    <SparklesIcon className="h-5 w-5 text-amber-200" />
                    <p className="mt-2 line-clamp-1 text-sm font-semibold">{offer.title || "Student offer"}</p>
                    <p className="mt-1 line-clamp-1 text-xs text-slate-400">{offer.consultancyName}</p>
                  </Link>
                ))}
              </div>
            )}
          </SectionShell>

          <SectionShell title="Activity" action={<Link to="/student/activity" className="text-sm font-semibold text-cyan-200 hover:text-cyan-100">Open</Link>}>
            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-slate-950/45 p-3">
                <span>Saved consultancies</span>
                <strong className="text-white">{wishlist.length}</strong>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-slate-950/45 p-3">
                <span>Profile completion</span>
                <strong className="text-white">{completion}%</strong>
              </div>
            </div>
          </SectionShell>
        </div>
      </div>
    </main>
  );
}
